import { effect, EffectRef, Injectable, signal } from '@angular/core';

export interface Theme {
  id: string;
  primary: string;
  displayName: string;
}

const THEME_STORAGE_KEY = 'selectedThemeId';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly themes: Theme[] = [
    { id: 'blue', primary: '#4957ad', displayName: 'Deep Blue' },
    { id: 'orange', primary: '#ff7a30', displayName: 'Orange' },
    { id: 'cyan', primary: '#60b5ff', displayName: 'Cyan' },
  ];

  // currentTheme signal
  currentTheme = signal<Theme>(this.themes[0]);

  private updateThemeClassRef!: EffectRef;

  constructor() {
    // initialize from storage (persisted theme) if present
    const stored = this.readStoredThemeId();
    if (stored) {
      const t = this.themes.find((x) => x.id === stored);
      if (t) this.currentTheme.set(t);
    }

    // create the effect and keep the EffectRef
    this.updateThemeClassRef = effect(() => {
      const theme = this.currentTheme();
      // remove any previous theme classes
      document.body.classList.remove(...this.themes.map((t) => `${t.id}-theme`));
      document.body.classList.add(`${theme.id}-theme`);
    });
  }

  getThemes(): Theme[] {
    return this.themes;
  }

  setTheme(themeId: string): void {
    const theme = this.themes.find((t) => t.id === themeId);
    if (theme) {
      this.currentTheme.set(theme);
      this.storeThemeId(themeId);
    }
  }

  toggleNextTheme(): void {
    const idx = this.themes.findIndex((t) => t.id === this.currentTheme().id);
    const next = this.themes[(idx + 1) % this.themes.length];
    this.setTheme(next.id);
  }

  destroyEffect(): void {
    // EffectRef has a destroy() method
    this.updateThemeClassRef?.destroy?.();
  }

  // ---- persistence helpers ----
  private storeThemeId(themeId: string) {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, themeId);
    } catch (err) {
      // storage might be disabled in some environments; ignore silently
      console.warn('Could not persist theme to localStorage', err);
    }
  }

  private readStoredThemeId(): string | null {
    try {
      return localStorage.getItem(THEME_STORAGE_KEY);
    } catch (err) {
      return null;
    }
  }
}
